import { useId } from 'react'
import { useAcoesDisponiveis } from '../../../api/queries/gmuds'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Carregando, EstadoVazio } from '../../../components/Estados'
import { TEXTOS } from '../../../lib/glossario'
import type { AcaoDisponivel } from '../../../types/gmud'
import { ehDestrutiva } from './acoesDestrutivas'
import { DialogoDeConfirmacao } from './DialogoDeConfirmacao'
import { DialogoDeMotivo } from './DialogoDeMotivo'
import { useAcionarTransicao } from './useAcionarTransicao'

interface Props {
  gmudId: number
  /** O que dizer quando o servidor não oferece ação nenhuma. Ver `SEM_ACOES_NO_HISTORICO`. */
  semAcoes?: { titulo: string; ajuda: string }
}

/**
 * Os botões de ação da solicitação — desenhados a partir do que o servidor mandou.
 *
 * **Não existe aqui nenhum `if` sobre status ou papel.** A lista inteira, incluindo o que
 * está desabilitado e por quê, vem de `GET /gmuds/{id}/transicoes-possiveis`, calculado
 * pelo mesmo código que a máquina de estados usa para decidir. Foi a divergência entre a
 * regra do backend e a cópia dela no frontend que, no sistema anterior, fez o menu
 * oferecer "Aprovar" para quem o servidor recusava — e ninguém percebeu porque a tela
 * mostrava um erro genérico.
 *
 * Ação bloqueada continua visível, desabilitada e com o motivo escrito por extenso: some
 * é pior, porque o usuário fica sem saber se falta um preenchimento ou se falta permissão.
 *
 * Ação que desfaz trabalho já feito sai em vermelho e pede confirmação — cor e pausa são
 * decisão de apresentação (ver `acoesDestrutivas.ts`), nunca de autorização.
 *
 * A decisão de "executar direto, pedir motivo ou confirmar" NÃO mora mais aqui: mora em
 * `useAcionarTransicao`, compartilhado com o menu de cada cartão do quadro. Duas telas
 * decidindo a mesma coisa por conta própria é como a divergência acima começou.
 *
 * @param props identificador da solicitação e o texto do caso sem ação
 * @returns o painel de ações
 */
export function BotoesTransicao({ gmudId, semAcoes = TEXTOS.semAcoes }: Props) {
  const { data: acoes, isPending, isError, error } = useAcoesDisponiveis(gmudId)
  const transicao = useAcionarTransicao(gmudId)
  const base = useId()

  if (isPending) {
    return <Carregando texto="Verificando o que você pode fazer…" />
  }
  if (isError) {
    return <AvisoDeErro erro={error} titulo="Não foi possível carregar as ações disponíveis" />
  }
  if (acoes.length === 0) {
    return <EstadoVazio titulo={semAcoes.titulo} ajuda={semAcoes.ajuda} />
  }

  const primeiraHabilitada = acoes.find((acao) => acao.habilitado)

  return (
    <section aria-label="Ações disponíveis" className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {acoes.map((acao) => (
          <div key={acao.comando} className="max-w-xs">
            <Botao
              aparencia={aparenciaDa(acao, acao === primeiraHabilitada)}
              disabled={!acao.habilitado || transicao.executando}
              aria-describedby={acao.impedimentos.length > 0 ? `${base}-${acao.comando}` : undefined}
              onClick={() => transicao.acionar(acao)}
            >
              {acao.rotulo}
            </Botao>
            {acao.impedimentos.length > 0 && (
              <ul id={`${base}-${acao.comando}`} className="mt-1 space-y-0.5 text-xs text-slate-600">
                {acao.impedimentos.map((impedimento) => (
                  <li key={impedimento}>{impedimento}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {transicao.falhou && !transicao.aguardandoMotivo && !transicao.aguardandoConfirmacao && (
        <AvisoDeErro erro={transicao.erro} titulo="A ação não foi concluída" />
      )}

      {transicao.aguardandoMotivo && (
        <DialogoDeMotivo
          acao={transicao.aguardandoMotivo}
          destrutiva={ehDestrutiva(transicao.aguardandoMotivo.comando)}
          enviando={transicao.executando}
          erro={transicao.falhou ? transicao.erro : null}
          aoConfirmar={transicao.confirmarComMotivo}
          aoCancelar={transicao.cancelar}
        />
      )}

      {transicao.aguardandoConfirmacao && (
        <DialogoDeConfirmacao
          acao={transicao.aguardandoConfirmacao}
          enviando={transicao.executando}
          erro={transicao.falhou ? transicao.erro : null}
          aoConfirmar={transicao.confirmar}
          aoCancelar={transicao.cancelar}
        />
      )}
    </section>
  )
}

/**
 * A aparência do botão.
 *
 * Uma ação em destaque por vez, e é a primeira habilitada que o servidor devolveu — a matriz de
 * transições já vem na ordem do processo, então a primeira é a que continua o fluxo. Destrutiva
 * ganha o vermelho mesmo sendo a primeira: se aprovar e recusar saíssem iguais, a diferença
 * entre elas ficaria só no texto, lido depois do clique.
 *
 * @param acao a ação oferecida pelo servidor
 * @param emDestaque se é a primeira habilitada da lista
 * @returns a aparência do componente `Botao`
 */
function aparenciaDa(acao: AcaoDisponivel, emDestaque: boolean): 'primario' | 'perigo' | 'secundario' {
  if (ehDestrutiva(acao.comando)) {
    return 'perigo'
  }
  return emDestaque ? 'primario' : 'secundario'
}
